import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchCmsCollection } from '../cms';
import LightboxModal from '../components/LightboxModal';

export default function HomePage() {
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeImageIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    fetchCmsCollection('services').then(data => setServices(data));
    fetchCmsCollection('projects').then(data => setProjects(data));
  }, []);

  const galleryImages = [
    "https://assets.auxodev.dk/assets/4aae8b48-1e4d-42c2-9336-5940e0b091a6/th-byg-34-scaled.jpg",
    "https://assets.auxodev.dk/assets/4aae8b48-1e4d-42c2-9336-5940e0b091a6/th-byg-16-scaled.jpg",
    "https://assets.auxodev.dk/assets/4aae8b48-1e4d-42c2-9336-5940e0b091a6/nyt-tag-jyllandsgade-i-horsens-scaled-1.jpg",
    "https://assets.auxodev.dk/assets/4aae8b48-1e4d-42c2-9336-5940e0b091a6/nye-vinduer-og-doere-3.jpg",
  ];

  return (
    <div className="bg-white">
      <Helmet>
        <title>Tømrer i Vejle og Horsens. Mere end 25 års erfaring. Kontakt os nu</title>
        <meta 
          name="description" 
          content="Skal du bruge en dygtig tømrer i Vejle eller Horsens? Vi laver kvalitetsløsninger med kyndig rådgivning. Tag kontakt i dag" 
        />
        <meta property="og:title" content="Tømrer i Vejle og Horsens. Mere end 25 års erfaring. Kontakt os nu" />
        <meta property="og:description" content="Skal du bruge en dygtig tømrer i Vejle eller Horsens? Vi laver kvalitetsløsninger med kyndig rådgivning. Tag kontakt i dag" />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/4aae8b48-1e4d-42c2-9336-5940e0b091a6/th-byg-34-scaled.jpg" />
      </Helmet>

      {/* SECTION 1: HERO SECTION (Exact KBM Byg visual layout from design spec screenshot) */}
      <section className="relative min-h-[550px] lg:min-h-[780px] bg-cover bg-center flex items-center bg-[#11161a] text-white py-16 sm:py-20 lg:py-32 overflow-hidden">
        {/* Background Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=80,format=auto/assets/4aae8b48-1e4d-42c2-9336-5940e0b091a6/th-byg-34-scaled.jpg"
            alt="Ståltage i Vejle, Horsens og Hedensted"
            className="h-full w-full object-cover object-center opacity-60 scale-105"
          />
          <div className="absolute inset-0 bg-black/35" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/25 to-transparent" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-6 sm:px-8 w-full">
          <div className="max-w-3xl text-left">
            {/* H1 Main Heading */}
            <h1 className="font-black text-2xl sm:text-4xl lg:text-5xl uppercase tracking-tight leading-none text-white">
              DIN TØMRER I RASK MØLLE NÆR VEJLE OG HORSENS
            </h1>

            {/* H2 Subheadline */}
            <h2 className="font-bold text-sm sm:text-base uppercase tracking-wider text-zinc-200 mt-4">
              STÅR DU OG MANGLER EN KVALITETSBEVIDST TØMRERMESTER TIL DIN OPGAVE?
            </h2>

            {/* Paragraph Body */}
            <p className="font-normal text-sm sm:text-base text-zinc-300 mt-4 leading-relaxed max-w-2xl">
              Hos T.H Byg står vi klar til at løse din opgave, uanset om du er privat eller erhverv. Vi tilbyder skarpe priser til alle kunder og mere end 25 års erfaring med ståltag, tilbygninger og renoveringer. Få et tilbud i dag.
            </p>

            {/* CTA Buttons - Pill shape */}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 bg-[#d92323] hover:bg-[#b81b1b] text-white font-extrabold text-xs sm:text-sm uppercase tracking-wide px-7 py-3.5 rounded-full shadow-lg transition-all duration-200 hover:scale-105"
              >
                <span>Få et uforpligtende tilbud</span>
                <span>&raquo;</span>
              </Link>
              <Link
                to="/staaltag"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-extrabold text-xs sm:text-sm uppercase tracking-wide px-7 py-3.5 rounded-full backdrop-blur-md transition-all"
              >
                <i className="fa-solid fa-house-chimney-crack text-[#d92323]" />
                <span>Se vores ståltage</span>
                <span>&raquo;</span>
              </Link>
            </div>

            {/* Fact Bullets */}
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-4 pt-6 border-t border-white/15 text-xs font-bold uppercase tracking-wider text-zinc-300">
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-check text-[#d92323]" />
                <span>Private & Erhverv</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-check text-[#d92323]" />
                <span>Ståltag specialist</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-check text-[#d92323]" />
                <span>Byg Garanti sikret</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: FEATURE SPLIT BLOCK 1 ("HVAD KAN VI?") - Edge-to-edge 2-col split */}
      <section className="w-full bg-[#f4f5f7] border-b border-slate-200">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Left Column: Light Gray Content Block */}
          <div className="p-10 sm:p-16 lg:p-24 flex flex-col justify-center space-y-8">
            <div className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-[#d92323]">
              <i className="fa-solid fa-hammer" />
              <span>T.H BYG V/ JACOB HARTVIG HANSEN</span>
            </div>

            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-tight text-slate-900">
              HVAD KAN VI?
            </h2>

            <div className="space-y-4 text-slate-700 text-sm sm:text-base leading-relaxed">
              <p>
                Hos T-H Byg har vi specialiseret os i de større tømreropgaver som renovering af huse og tage, lægning af nyt tag samt tilbygninger og ombygninger.
              </p>
              <p>
                Vores kunder er private boligejere, virksomheder og sommerhusejere i Vejle-området, på Hedensted- og Horsens-kanten. Vi hjælper også gerne dig, hvis du har et projekt, du vil have en erfaren og kvalitetsbevidst tømrer til at stå for.
              </p>
              <p>
                Uanset hvad din opgave byder på, kan du regne med, at vi leverer et tilfredsstillende resultat. Med vores mere end 25 års erfaring i branchen har vi prøvet lidt af hvert.
              </p>
            </div>

            <div>
              <Link
                to="/om-os"
                className="inline-flex items-center gap-3 rounded-full bg-[#d92323] px-7 py-3.5 text-sm font-extrabold uppercase tracking-wide text-white shadow-md hover:bg-[#b81b1b] transition-all"
              >
                <span>LÆS MERE OM OS</span>
                <i className="fa-solid fa-arrow-right" />
              </Link>
            </div>
          </div>

          {/* Right Column: Edge-to-Edge Media Block */}
          <div className="relative min-h-[380px] lg:min-h-[500px]">
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=80,format=auto/assets/4aae8b48-1e4d-42c2-9336-5940e0b091a6/th-byg-07-scaled.jpg"
              alt="Tømrerarbejde udført af T.H Byg"
              className="absolute inset-0 w-full h-full object-cover cursor-pointer"
              onClick={() => {
                setActiveIndex(1);
                setLightboxOpen(true);
              }}
            />
          </div>
        </div>
      </section>

      {/* SECTION 3: FEATURE SPLIT BLOCK 2 ("SPECIALISTER I STÅLTAG") - Reversed 2-col split */}
      <section className="w-full bg-white border-b border-slate-200">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Left Column: Edge-to-Edge Media Block */}
          <div className="relative min-h-[380px] lg:min-h-[500px] order-2 lg:order-1">
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=80,format=auto/assets/4aae8b48-1e4d-42c2-9336-5940e0b091a6/th-byg-34-scaled.jpg"
              alt="Ståltag montering af T.H Byg"
              className="absolute inset-0 w-full h-full object-cover cursor-pointer"
              onClick={() => {
                setActiveIndex(0);
                setLightboxOpen(true);
              }}
            />
            <div className="absolute top-6 left-6 rounded bg-[#d92323] px-4 py-2 text-xs font-black uppercase tracking-wider text-white shadow-lg">
              ★ Hovedspeciale: Ståltag
            </div>
          </div>

          {/* Right Column: Light Gray Content Block */}
          <div className="p-10 sm:p-16 lg:p-24 flex flex-col justify-center space-y-8 bg-[#f4f5f7] order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-[#d92323]">
              <i className="fa-solid fa-shield-halved" />
              <span>METROTILE & DECRA TAGLØSNINGER</span>
            </div>

            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-tight text-slate-900">
              SPECIALISTER I STÅLTAG
            </h2>

            <p className="text-slate-700 text-sm sm:text-base leading-relaxed">
              Hos T-H Byg har vi specialiseret os i lægning af nyt ståltag i Metrotile eller Decra. Et ståltag er den moderne, holdbare og økonomisk fornuftige tagløsning til boliger, sommerhuse og erhverv i Vejle, Horsens og Hedensted.
            </p>

            {/* Custom Icon Bullet List (KBM Byg feature style) */}
            <div className="space-y-3 pt-2">
              <div className="flex items-start gap-3">
                <i className="fa-solid fa-circle-check text-[#d92323] text-lg mt-0.5" />
                <div>
                  <h3 className="font-extrabold text-slate-900 text-sm uppercase">Økonomisk & Bæredygtig fordel</h3>
                  <p className="text-xs text-slate-600">Stor besparelse sammenlignet med tungt tegl uden at gå på kompromis med udseendet.</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <i className="fa-solid fa-circle-check text-[#d92323] text-lg mt-0.5" />
                <div>
                  <h3 className="font-extrabold text-slate-900 text-sm uppercase">Montering ovenpå eksisterende tag</h3>
                  <p className="text-xs text-slate-600">Ståltagets lave vægt gør det ideelt til montering direkte ovenpå gamle eternittage.</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <i className="fa-solid fa-circle-check text-[#d92323] text-lg mt-0.5" />
                <div>
                  <h3 className="font-extrabold text-slate-900 text-sm uppercase">Op til 40 års produktgaranti</h3>
                  <p className="text-xs text-slate-600">Lang levetid og modstandsdygtigt over for storm og kraftigt nedbør.</p>
                </div>
              </div>
            </div>

            <div>
              <Link
                to="/staaltag"
                className="inline-flex items-center gap-3 rounded-full bg-[#d92323] px-7 py-3.5 text-sm font-extrabold uppercase tracking-wide text-white shadow-md hover:bg-[#b81b1b] transition-all"
              >
                <span>LÆS MERE OM STÅLTAG</span>
                <i className="fa-solid fa-arrow-right" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4: NAVIGATION CARD GRID (Industrial Craftsman 3 Navigation Cards) */}
      <section className="py-12 lg:py-24 bg-white border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-[40px]">
            {/* Card 1: Arbejdsområder */}
            <Link
              to="/ydelser"
              className="group relative aspect-[4/3] sm:aspect-[4/5] overflow-hidden bg-slate-900"
            >
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/4aae8b48-1e4d-42c2-9336-5940e0b091a6/th-byg-16-scaled.jpg"
                alt="Arbejdsområder"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/30 transition-colors group-hover:bg-black/45" />
              <div className="absolute inset-0 flex items-center justify-center p-4 z-10">
                <h2 className="text-white font-extrabold uppercase tracking-wider text-lg lg:text-2xl text-center drop-shadow-md">
                  Arbejdsområder
                </h2>
              </div>
              {/* Red line design element on hover */}
              <div className="absolute bottom-0 inset-x-0 h-1.5 bg-[#d92323] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left z-20" />
            </Link>

            {/* Card 2: Referencer */}
            <Link
              to="/referencer"
              className="group relative aspect-[4/3] sm:aspect-[4/5] overflow-hidden bg-slate-900"
            >
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/4aae8b48-1e4d-42c2-9336-5940e0b091a6/nyt-tag-jyllandsgade-i-horsens-scaled-1.jpg"
                alt="Referencer"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/30 transition-colors group-hover:bg-black/45" />
              <div className="absolute inset-0 flex items-center justify-center p-4 z-10">
                <h2 className="text-white font-extrabold uppercase tracking-wider text-lg lg:text-2xl text-center drop-shadow-md">
                  Referencer
                </h2>
              </div>
              {/* Red line design element on hover */}
              <div className="absolute bottom-0 inset-x-0 h-1.5 bg-[#d92323] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left z-20" />
            </Link>

            {/* Card 3: Om os */}
            <Link
              to="/om-os"
              className="group relative aspect-[4/3] sm:aspect-[4/5] overflow-hidden bg-slate-900"
            >
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/4aae8b48-1e4d-42c2-9336-5940e0b091a6/th-byg-07-scaled.jpg"
                alt="Om os"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/30 transition-colors group-hover:bg-black/45" />
              <div className="absolute inset-0 flex items-center justify-center p-4 z-10">
                <h2 className="text-white font-extrabold uppercase tracking-wider text-lg lg:text-2xl text-center drop-shadow-md">
                  Om os
                </h2>
              </div>
              {/* Red line design element on hover */}
              <div className="absolute bottom-0 inset-x-0 h-1.5 bg-[#d92323] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left z-20" />
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 5: TESTIMONIALS SECTION ("Det siger vores kunder") - Designed strictly per design reference analysis */}
      <section className="bg-white py-12 md:py-20 px-4 border-b border-slate-200">
        <div className="max-w-6xl mx-auto">
          {/* Section Title */}
          <h2 className="text-xl md:text-2xl font-black uppercase tracking-wider text-zinc-900 text-center mb-10 md:mb-14">
            Det siger vores kunder
          </h2>

          {/* Testimonials Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 items-start">
            {/* Testimonial 1 */}
            <article className="flex flex-col gap-3">
              {/* Rating Stars */}
              <div className="flex items-center gap-1 text-[#f95722]">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>

              {/* Headline */}
              <h3 className="text-sm md:text-base font-extrabold uppercase text-zinc-900 tracking-tight">
                "Problemfrit Forløb"
              </h3>

              {/* Content */}
              <div className="text-xs md:text-sm text-zinc-600 leading-relaxed space-y-3">
                <p>Vi fik sat nye vinduer i husets facade. Vinduerne blev sat i på den aftalte dato.</p>
                <p>Firmaet er meget professionelt og udført godt håndværksmæssigt arbejde. Priserne er meget fornuftig og jeg blev mødt med venlighed og hjælpsomhed.</p>
              </div>

              {/* Author */}
              <span className="text-xs md:text-sm font-bold text-zinc-900 mt-2">
                Line Solgaard
              </span>
            </article>

            {/* Testimonial 2 */}
            <article className="flex flex-col gap-3">
              {/* Rating Stars */}
              <div className="flex items-center gap-1 text-[#f95722]">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>

              {/* Headline */}
              <h3 className="text-sm md:text-base font-extrabold uppercase text-zinc-900 tracking-tight">
                "Gennemført Kvalitet"
              </h3>

              {/* Content */}
              <div className="text-xs md:text-sm text-zinc-600 leading-relaxed space-y-3">
                <p>Jacob lagde nyt ståltag på vores hus i Vejle. Arbejdet blev udført hurtigt, præcist og til den aftalte pris.</p>
                <p>Aftalerne blev overholdt til punkt og prikke, og oprydningen var helt i top. Kan klart anbefales!</p>
              </div>

              {/* Author */}
              <span className="text-xs md:text-sm font-bold text-zinc-900 mt-2">
                Morten & Karin Vedel
              </span>
            </article>

            {/* Testimonial 3 */}
            <article className="flex flex-col gap-3">
              {/* Rating Stars */}
              <div className="flex items-center gap-1 text-[#f95722]">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>

              {/* Headline */}
              <h3 className="text-sm md:text-base font-extrabold uppercase text-zinc-900 tracking-tight">
                "Super Tømrerarbejde"
              </h3>

              {/* Content */}
              <div className="text-xs md:text-sm text-zinc-600 leading-relaxed space-y-3">
                <p>Vi har benyttet T.H Byg til både ny trappe og udskiftning af vinduer i Rask Mølle.</p>
                <p>Jacob er en fantastisk håndværker, som man trygt kan overlade sin bolig til. Vi giver vores varmeste anbefalinger.</p>
              </div>

              {/* Author */}
              <span className="text-xs md:text-sm font-bold text-zinc-900 mt-2">
                Henrik Lauridsen
              </span>
            </article>
          </div>
        </div>
      </section>

      {/* SECTION 6: UDVALGTE PROJEKTER & REFERENCER */}
      <section className="pt-16 sm:pt-24 pb-32 sm:pb-44 lg:pb-56 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-[#d92323] mb-2">
                <i className="fa-solid fa-camera" />
                <span>Dokumenteret håndværk</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-tight text-slate-900">
                UDVALGTE PROJEKTER
              </h2>
            </div>
            <Link
              to="/projekter"
              className="mt-4 md:mt-0 inline-flex items-center gap-2 text-sm font-extrabold uppercase tracking-wide text-[#d92323] hover:underline"
            >
              <span>SE HELE PORTFØLJEN</span>
              <i className="fa-solid fa-arrow-right text-xs" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.slice(0, 3).map((proj, idx) => (
              <div
                key={proj.id || idx}
                className="group relative rounded-none overflow-hidden bg-slate-900 shadow-md hover:shadow-2xl transition-all cursor-pointer border border-slate-200"
                onClick={() => {
                  setActiveIndex(idx);
                  setLightboxOpen(true);
                }}
              >
                <div className="aspect-[4/5] overflow-hidden relative rounded-none">
                  <img
                    src={proj.image && proj.image[0] ? proj.image[0] : galleryImages[idx % galleryImages.length]}
                    alt={proj.title || `Projekt ${idx + 1}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 rounded-none"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal for fullscreen image views */}
      <LightboxModal
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={galleryImages}
        currentIndex={activeImageIndex}
        onNavigate={(idx) => setActiveIndex(idx)}
      />
    </div>
  );
}
